import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from 'cn';
import { X } from 'lucide-react';
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return <DialogPrimitive.Overlay className={cn('data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60', className)} {...props} data-sentry-element="DialogPrimitive.Overlay" data-sentry-component="DialogOverlay" data-sentry-source-file="dialog.tsx" />;
}
interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean;
  dialogCloseProps?: DialogPrimitive.DialogCloseProps;
  /**
   * When false, blocks Escape + outside-click close channels and disables
   * the built-in close button. Use during in-flight operations to protect
   * component state from dismissal-driven unmount.
   */
  dismissible?: boolean;
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Content>>;
}
function DialogContent({
  className,
  children,
  hideCloseButton,
  dialogCloseProps,
  dismissible = true,
  onEscapeKeyDown,
  onInteractOutside,
  ref,
  ...props
}: DialogContentProps) {
  return <DialogPortal data-sentry-element="DialogPortal" data-sentry-component="DialogContent" data-sentry-source-file="dialog.tsx">
      <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="dialog.tsx" />
      <DialogPrimitive.Content ref={ref} className={cn('bg-background', 'border-tertiary-300 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-xl border shadow-sm duration-200', 'sm:max-w-lg', 'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95', className)} onEscapeKeyDown={event => {
      if (!dismissible) event.preventDefault();
      onEscapeKeyDown?.(event);
    }} onInteractOutside={event => {
      if (!dismissible) event.preventDefault();
      onInteractOutside?.(event);
    }} {...props} data-sentry-element="DialogPrimitive.Content" data-sentry-source-file="dialog.tsx">
        {children}
        {!hideCloseButton && <DialogPrimitive.Close {...dialogCloseProps} disabled={dialogCloseProps?.disabled ?? !dismissible} className={cn('ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-30', dialogCloseProps?.className)}>
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>}
      </DialogPrimitive.Content>
    </DialogPortal>;
}
function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-tertiary-300 border-b', 'flex flex-col space-y-1 px-3 py-3.5 text-center sm:text-left', className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
}
function DialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('text-sm leading-5', 'min-h-0 flex-1 px-3', '[&::-webkit-scrollbar]:w-1', '[&::-webkit-scrollbar-thumb]:rounded-xl', '[&::-webkit-scrollbar-thumb]:bg-tertiary-300', '[&::-webkit-scrollbar-track]:rounded-xl', '[&::-webkit-scrollbar-track]:bg-tertiary-100',
  // Add max-height and overflow-y-auto for scrolling
  'max-h-[calc(100vh-420px)] overflow-y-auto',
  // CSS tricky to prevent rings (shadows) from input or other form elements from getting
  // cut off when overflow occurs
  '-my-1 py-1', className)} {...props} data-sentry-component="DialogBody" data-sentry-source-file="dialog.tsx" />;
}
function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col-reverse gap-2 px-3 pb-3 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2', className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
}
interface DialogTitleProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Title>>;
}
function DialogTitle({
  className,
  ref,
  ...props
}: DialogTitleProps) {
  return <DialogPrimitive.Title ref={ref} className={cn('text-secondary-900 text-sm leading-5 font-medium', className)} {...props} data-sentry-element="DialogPrimitive.Title" data-sentry-component="DialogTitle" data-sentry-source-file="dialog.tsx" />;
}
interface DialogDescriptionProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Description>>;
}
function DialogDescription({
  className,
  ref,
  ...props
}: DialogDescriptionProps) {
  return <DialogPrimitive.Description ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} data-sentry-element="DialogPrimitive.Description" data-sentry-component="DialogDescription" data-sentry-source-file="dialog.tsx" />;
}
export { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };